<template>
  <div class="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-2rem)]">
    <div class="flex items-center space-x-4 mb-6">
      <h2 class="text-xl font-bold text-gray-800">开放问诊记录</h2>
      <div class="bg-gray-100 p-1 rounded-lg flex text-sm font-medium">
        <button 
          @click="changeSource('user')"
          class="px-3 py-1.5 rounded-md transition-all"
          :class="sourceType === 'user' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          机构问诊
        </button>
        <button 
          @click="changeSource('patient')"
          v-if="canViewPatientHistory"
          class="px-3 py-1.5 rounded-md transition-all"
          :class="sourceType === 'patient' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          用户问诊
        </button>
      </div>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm text-gray-600">
        <thead class="bg-gray-50 text-gray-500 font-medium">
          <tr>
            <th class="py-3 px-4">问诊ID</th>
            <th class="py-3 px-4">患者</th>
            <th class="py-3 px-4">手机号</th>
            <th class="py-3 px-4">性别</th>
            <th class="py-3 px-4">年龄</th>
            <th class="py-3 px-4">主诉</th>
            <th class="py-3 px-4">辨证结果</th>
            <th class="py-3 px-4">问诊时间</th>
            <th class="py-3 px-4 text-center">操作</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-if="!loading && records.length === 0">
            <td colspan="9" class="py-8 px-4 text-center text-gray-400 text-sm">
              暂无开放问诊记录
            </td>
          </tr>
          <tr v-for="item in records" :key="item.id" class="hover:bg-gray-50 transition">
            <td class="py-4 px-4">{{ item.id }}</td>
            <td class="py-4 px-4">{{ item.patient_name || '' }}</td>
            <td class="py-4 px-4">{{ item.patient_mobile || '' }}</td>
            <td class="py-4 px-4">{{ item.patient_gender || '' }}</td>
            <td class="py-4 px-4">{{ item.patient_age || '' }}</td>
            <td class="py-4 px-4 max-w-[420px]">
              <div class="truncate" :title="item.complaint || ''">{{ item.complaint || '' }}</div>
            </td>
            <td class="py-4 px-4">{{ item.diagnosis || '' }}</td>
            <td class="py-4 px-4">{{ item.created_at || '' }}</td>
            <td class="py-4 px-4 flex justify-center space-x-2">
              <button class="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition" @click="openDetailModal(item)">查看</button>
              <button class="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition" @click="openDeleteConfirm(item)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex justify-between items-center mt-6 text-sm text-gray-500">
      <div class="flex items-center space-x-2">
        <span>第 {{ page }}/{{ totalPages }} 页 共 {{ total }} 条</span>
      </div>
      <div class="flex space-x-2">
        <button
          class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          :disabled="loading || page <= 1"
          @click="goToPrevPage"
        >
          上一页
        </button>
        <button
          class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          :disabled="loading || page >= totalPages || !hasMore"
          @click="goToNextPage"
        >
          下一页
        </button>
      </div>
    </div>
    <div
      v-if="showDetailModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-800">问诊详情</h3>
          <button class="text-gray-400 hover:text-gray-600" @click="closeDetailModal">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div v-if="detailLoading" class="py-8 text-center text-gray-500 text-sm">
          加载中...
        </div>
        <div v-else-if="detailError" class="py-4 text-red-500 text-sm">
          {{ detailError }}
        </div>
        <div v-else-if="detailData" class="space-y-6">
          <div class="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <span class="text-gray-400">问诊ID：</span>
              <span>{{ detailData.id }}</span>
            </div>
            <div>
              <span class="text-gray-400">问诊时间：</span>
              <span>{{ detailData.created_at || '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">患者姓名：</span>
              <span>{{ detailRecord && detailRecord.patient_name ? detailRecord.patient_name : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">手机号：</span>
              <span>{{ detailRecord && detailRecord.patient_mobile ? detailRecord.patient_mobile : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">性别：</span>
              <span>{{ detailRecord && detailRecord.patient_gender ? detailRecord.patient_gender : '' }}</span>
            </div>
            <div>
              <span class="text-gray-400">年龄：</span>
              <span>{{ detailRecord && (detailRecord.patient_age !== undefined && detailRecord.patient_age !== null) ? detailRecord.patient_age : '' }}</span>
            </div>
          </div>
          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">主诉</h4>
            <div class="bg-gray-50 px-3 py-2 rounded text-gray-700 whitespace-pre-line text-sm">
              {{ detailData.complaint || '' }}
            </div>
          </div>
          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">问答记录</h4>
            <div v-if="detailData.answers && detailData.answers.length" class="space-y-3 text-sm">
              <div
                v-for="(answer, index) in detailData.answers"
                :key="index"
                class="p-3 rounded-lg bg-gray-50"
              >
                <div class="text-gray-500 text-xs mb-1">
                  第 {{ index + 1 }} 题
                </div>
                <div class="text-gray-800 mb-1">
                  {{ answer.question_content }}
                </div>
                <div class="text-gray-600">
                  回复：{{ answer.reply_content }}
                </div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-sm">
              暂无问答记录
            </div>
          </div>
          <div v-if="detailData.tongue_image" class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">舌苔图</h4>
            <div class="bg-gray-50 px-3 py-3 rounded">
              <img 
                :src="detailData.tongue_image" 
                alt="舌苔图" 
                class="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                @click="openImagePreview(detailData.tongue_image)"
              />
            </div>
          </div>
          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">辨证结果</h4>
            <div v-if="detailData.diagnosis" class="space-y-3 text-sm text-gray-700">
              <div>
                <div class="text-gray-400 mb-1">证型</div>
                <div class="bg-gray-50 px-3 py-2 rounded whitespace-pre-line">{{ detailData.diagnosis.diagnosis || '' }}</div>
              </div>
              <div>
                <div class="text-gray-400 mb-1">症候群</div>
                <div class="bg-gray-50 px-3 py-2 rounded whitespace-pre-line">{{ detailData.diagnosis.syndrome || '' }}</div>
              </div>
              <div>
                <div class="text-gray-400 mb-1">方剂</div>
                <div class="bg-gray-50 px-3 py-2 rounded whitespace-pre-line">{{ detailData.diagnosis.prescription || '' }}</div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-sm">
              暂无辨证结果
            </div>
          </div>
          <div class="flex justify-end mt-4">
            <button class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition" @click="closeDetailModal">关闭</button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-4">确认删除</h3>
        <p class="text-gray-600 mb-6">
          确定要删除该开放问诊记录吗？
        </p>
        <div class="flex justify-end space-x-3">
          <button
            @click="closeDeleteConfirm"
            class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
            :disabled="deleting"
          >
            取消
          </button>
          <button
            @click="confirmDelete"
            class="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-sm disabled:opacity-70"
            :disabled="deleting"
          >
            {{ deleting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- 图片放大预览 -->
    <div 
      v-if="previewImageUrl" 
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90"
      @click="closeImagePreview"
    >
      <div class="relative max-w-full max-h-full p-4">
        <button 
          class="absolute top-0 right-0 p-2 text-white hover:text-gray-300"
          @click.stop="closeImagePreview"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img :src="previewImageUrl" class="max-w-full max-h-[90vh] object-contain rounded" @click.stop />
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest, parsePagination } from '@/utils/request'

export default {
  name: 'OpenDiagnosisRecords',
  data() {
    return {
      sourceType: 'user', // 'user' | 'patient'
      canViewPatientHistory: true,
      records: [],
      page: 1,
      totalPages: 1,
      total: 0,
      pageSize: 15,
      hasMore: false,
      loading: false,
      showDetailModal: false,
      detailLoading: false,
      detailError: '',
      detailData: null,
      detailRecord: null,
      showDeleteConfirm: false,
      deleting: false,
      recordToDelete: null,
      previewImageUrl: ''
    }
  },
  mounted() {
    this.loadPermissions()
    this.fetchHistory(1)
  },
  methods: {
    loadPermissions() {
      let cached = null
      try {
        const raw = localStorage.getItem('user_profile')
        if (raw) cached = JSON.parse(raw)
      } catch (e) {
        cached = null
      }
      const can_view = cached && typeof cached.can_view_patient_diagnosis_history === 'boolean'
        ? cached.can_view_patient_diagnosis_history
        : true
      this.canViewPatientHistory = can_view
      if (!can_view && this.sourceType === 'patient') {
        this.sourceType = 'user'
      }
    },
    changeSource(type) {
      if (this.sourceType === type || this.loading) return
      if (type === 'patient' && !this.canViewPatientHistory) {
        alert('暂无权限查看用户问诊记录')
        return
      }
      this.sourceType = type
      this.page = 1
      this.records = []
      this.fetchHistory(1)
    },
    async fetchHistory(targetPage = 1) {
      if (this.loading) return
      this.loading = true
      try {
        const response = await httpRequest('get', '/v1/diagnosis/open/history', {
          headers: {
            'Client-Type': '1'
          },
          query: {
            page: targetPage,
            role: this.sourceType
          }
        })
        const pagination = parsePagination(response, targetPage, 'data')
        this.records = pagination.list
        this.page = pagination.page
        this.hasMore = pagination.has_more

        const meta = response && response.data && response.data.meta ? response.data.meta : {}
        const p = meta.pagination || {}
        if (typeof p.total === 'number') {
          this.total = p.total
        } else {
          this.total = this.records.length
        }
        if (typeof p.per_page === 'number') {
          this.pageSize = p.per_page
        }
        if (typeof p.total_pages === 'number') {
          this.totalPages = p.total_pages
        } else {
          this.totalPages = this.hasMore ? this.page + 1 : this.page
        }
      } catch (error) {
        this.records = []
      } finally {
        this.loading = false
      }
    },
    goToPrevPage() {
      if (this.page <= 1 || this.loading) return
      const target = this.page - 1
      this.fetchHistory(target)
    },
    goToNextPage() {
      if (this.loading || this.page >= this.totalPages || !this.hasMore) return
      const target = this.page + 1
      this.fetchHistory(target)
    },
    openDetailModal(record) {
      if (!record || !record.id) return
      this.showDetailModal = true
      this.detailLoading = true
      this.detailError = ''
      this.detailData = null
      this.detailRecord = record
      this.fetchDetail(record.id)
    },
    closeDetailModal() {
      if (this.detailLoading) return
      this.showDetailModal = false
      this.detailRecord = null
    },
    openImagePreview(url) {
      if (!url) return
      this.previewImageUrl = url
    },
    closeImagePreview() {
      this.previewImageUrl = ''
    },
    async fetchDetail(sessionId) {
      try {
        const response = await httpRequest('get', `/v1/diagnosis/open/session/${sessionId}`, {
          headers: {
            'Client-Type': '1'
          }
        })
        this.detailData = response && response.data ? response.data : null
      } catch (error) {
        this.detailError = error && error.message ? error.message : '加载详情失败'
      } finally {
        this.detailLoading = false
      }
    },
    openDeleteConfirm(record) {
      if (!record || !record.id) return
      this.recordToDelete = record
      this.showDeleteConfirm = true
      this.deleting = false
    },
    closeDeleteConfirm() {
      if (this.deleting) return
      this.showDeleteConfirm = false
      this.recordToDelete = null
    },
    async confirmDelete() {
      if (!this.recordToDelete || !this.recordToDelete.id || this.deleting) {
        return
      }
      this.deleting = true
      try {
        await httpRequest('delete', `/v1/diagnosis/open/session/${this.recordToDelete.id}`, {
          headers: {
            'Client-Type': '1'
          }
        })
        this.showDeleteConfirm = false
        this.recordToDelete = null
        this.fetchHistory(this.page)
      } catch (error) {
        this.deleting = false
      }
    }
  }
}
</script>
